import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { useHoaDonBanHangExport } from '../hooks/useHoaDonBanHangExport';
import { MA_CHUNG_TU, QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

interface HoaDonBanHangSearchContextValue {
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
  ngayCt: string | undefined;
  setNgayCt: (value: string | undefined) => void;
  customerGroupUuid: string | undefined;
  setCustomerGroupUuid: (value: string | undefined) => void;
  isImportDialogOpen: boolean;
  openImportDialog: () => void;
  closeImportDialog: () => void;
  transactionOptions: { value: string; label: string }[];
}

const HoaDonBanHangSearchContext = createContext<HoaDonBanHangSearchContextValue | null>(null);

export function useHoaDonBanHangSearchContext() {
  const context = useContext(HoaDonBanHangSearchContext);

  if (!context) {
    throw new Error('useHoaDonBanHangSearchContext must be used within HoaDonBanHangSearchProvider');
  }

  return context;
}

export function HoaDonBanHangSearchProvider({ children }: { children: ReactNode }) {
  const { exportToExcel, canExport, isExporting } = useHoaDonBanHangExport();
  const [ngayCt, setNgayCt] = useState<string | undefined>(undefined);
  const [customerGroupUuid, setCustomerGroupUuid] = useState<string | undefined>(undefined);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const { data } = useCRUD({
    endpoint: `${QUERY_KEYS.MA_GIAO_DICH}?ma_ct=${MA_CHUNG_TU.HOA_DON.HOA_DON_BAN_HANG}`
  });

  const transactionOptions = useMemo(
    () =>
      data.map(item => ({
        value: item.uuid,
        label: item.ten_gd
      })),
    [data]
  );

  const value = useMemo(
    () => ({
      exportToExcel,
      canExport,
      isExporting,
      ngayCt,
      setNgayCt,
      customerGroupUuid,
      setCustomerGroupUuid,
      isImportDialogOpen,
      openImportDialog: () => setIsImportDialogOpen(true),
      closeImportDialog: () => setIsImportDialogOpen(false),
      transactionOptions
    }),
    [canExport, customerGroupUuid, exportToExcel, isExporting, isImportDialogOpen, ngayCt, transactionOptions]
  );

  return <HoaDonBanHangSearchContext.Provider value={value}>{children}</HoaDonBanHangSearchContext.Provider>;
}
